import React, {FC, useState, useEffect} from "react"

import Flex from "@irati/ui/src/Flex"
import {Body, H1, H3} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import authentication from "@irati/auth"
import {StoryRedemptions, StoryDealCard, Deal, PaymentCurrencyType} from "@irati/types"
import dealsService from "@irati/deals/services/dealsGraph"

import {LinearGradiantCard, NumberOfRedemptions} from "./styled"
import {CardRedemption, CARD_REDEMPTIONS} from "./Variants"

interface Props {
  currencyCode: PaymentCurrencyType
  deals?: StoryDealCard[]
  total?: StoryRedemptions
}

const Redemptions: FC<Props> = ({currencyCode, deals, total}) => {
  const [cardDeals, setCardDeals] = useState<CardRedemption[]>()

  const businessName = authentication.business().businessName
  const theme = useTheme()
  const {formatMessage, formatNumber} = useIntl()

  const getDeal = (deal: StoryDealCard, variant: CardRedemption) => {
    return dealsService.deal({uuid: deal.dealId}).then((resDeal: Deal) => {
      return {
        type: variant.type,
        title: variant.title,
        text: variant.text,
        logo: resDeal.thumbnailImage,
        name: resDeal.brandName,
        redemptions: deal.redemptions,
        savings: currencyCode === PaymentCurrencyType.Gbp ? deal.totalSavingsGbp : deal.totalSavingsAud
      }
    })
  }

  useEffect(() => {
    if (deals) {
      Promise.all(
        deals.map(async (deal) => {
          const variant = CARD_REDEMPTIONS.find((d) => d.type === deal.type)
          if (variant) {
            return await getDeal(deal, variant)
          }
        })
      ).then((data) => {
        setCardDeals(data)
      })
    }
  }, [])

  return (
    <Flex gap={1}>
      {cardDeals?.map((cardDeal: CardRedemption, i: number) => {
        const title = cardDeal?.title
        const text = cardDeal?.text
        const name = cardDeal?.name
        return (
          cardDeal && (
            <LinearGradiantCard marginBottom c1={theme.colors.primary1} c2={theme.colors.success} key={`yourPerkboxStoryRedemption-${i}`}>
              <Flex alignItems="center" direction="column" gap={1}>
                <NumberOfRedemptions justifyContent="center" alignItems="center">
                  {cardDeal.logo && <img src={formatMessage({id: cardDeal.logo})} height={100} width={100} />}
                </NumberOfRedemptions>
                <H3 color={theme.colors.greys.white}>
                  <FormattedMessage id={title} />
                </H3>
                <Body small maxWidth="100%" color={theme.colors.greys.white} textAlign="center">
                  <FormattedMessage
                    id={text}
                    values={{
                      businessName: (
                        <Body small display="inline" color={theme.colors.greys.white}>
                          <FormattedMessage id={businessName} />
                        </Body>
                      ),
                      dealName: (
                        <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                          <FormattedMessage id={name} />
                        </Body>
                      ),
                      redemptions: (
                        <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                          {formatNumber(cardDeal.redemptions!)}
                        </Body>
                      ),
                      savings: (
                        <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                          {formatCurrency({value: cardDeal.savings, currencyCode, isCents: false})}
                        </Body>
                      )
                    }}
                  />
                </Body>
              </Flex>
            </LinearGradiantCard>
          )
        )
      })}
      {total && (
        <LinearGradiantCard marginBottom c1={theme.colors.primary1} c2={theme.colors.success}>
          <Flex alignItems="center" direction="column" gap={1}>
            <NumberOfRedemptions justifyContent="center" alignItems="center">
              <H1 color={theme.colors.greys.white}>{formatNumber(total.redemptions!)}</H1>
            </NumberOfRedemptions>
            <H3 color={theme.colors.greys.white}>
              <FormattedMessage id="admin.components.yourPerkboxStory.numberOfRedemptions.title" />
            </H3>
            <Body small maxWidth="100%" color={theme.colors.greys.white} textAlign="center">
              <FormattedMessage
                id="admin.components.yourPerkboxStory.numberOfRedemptions.body"
                values={{
                  businessName: (
                    <Body small display="inline" color={theme.colors.greys.white}>
                      <FormattedMessage id={businessName} />
                    </Body>
                  ),
                  unique: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      <FormattedMessage
                        id="admin.components.yourPerkboxStory.numberOfRedemptions.unique"
                        values={{unique: formatNumber(total.uniqueDeals!)}}
                      />
                    </Body>
                  ),
                  redemptions: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatNumber(total.redemptions!)}
                    </Body>
                  ),
                  savings: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatCurrency({
                        isCents: false,
                        value: currencyCode === PaymentCurrencyType.Gbp ? total.totalSavingsGbp : total.totalSavingsAud,
                        currencyCode
                      })}
                    </Body>
                  )
                }}
              />
            </Body>
          </Flex>
        </LinearGradiantCard>
      )}
    </Flex>
  )
}

export default Redemptions
